import { computed, onMounted, type Ref } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

import { getConsistencyAssetTypeLabel } from './materialLibrary'
import type { AssetTab, KnowledgeQueryForm, MaterialLibraryFilters } from './types'

interface UseKnowledgeBaseRouteStateOptions {
  route: RouteLocationNormalizedLoaded
  router: Router
  activeTab: Ref<AssetTab>
  queryForm: KnowledgeQueryForm
  imageFilters: MaterialLibraryFilters
}

function truncateQuery(value: string, maxLength = 240) {
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength)
}

export function useKnowledgeBaseRouteState(options: UseKnowledgeBaseRouteStateOptions) {
  const linkedProjectId = computed(() => {
    const raw = Number(options.route.query.projectId)
    return Number.isFinite(raw) && raw > 0 ? raw : null
  })

  const linkedStage = computed(() => {
    const raw = String(options.route.query.stage || '').trim()
    return raw || null
  })

  const returnPath = computed(() => {
    const raw = String(options.route.query.return || '').trim()
    return raw || null
  })

  const linkedSceneNumber = computed(() => {
    const raw = Number(options.route.query.sceneNumber)
    return Number.isFinite(raw) && raw > 0 ? raw : null
  })

  const linkedAssetType = computed<'character' | 'scene' | 'prop' | null>(() => {
    const raw = String(options.route.query.assetType || '').trim()
    if (raw === 'character' || raw === 'scene' || raw === 'prop') {
      return raw
    }
    return null
  })

  const linkedAssetId = computed(() => String(options.route.query.assetId || '').trim() || null)
  const linkedAssetName = computed(() => String(options.route.query.assetName || '').trim() || '')

  const knowledgeMode = computed(() => String(options.route.query.mode || '').trim())
  const isSceneImageMode = computed(() => knowledgeMode.value === 'scene-image' && Boolean(linkedSceneNumber.value))
  const isConsistencyAssetImageMode = computed(() => knowledgeMode.value === 'consistency-asset-image' && Boolean(linkedAssetType.value && linkedAssetId.value))
  const linkedAudioRole = computed<'narration' | 'bgm' | null>(() => {
    const raw = String(options.route.query.audioRole || '').trim()
    if (raw === 'narration' || raw === 'bgm') {
      return raw
    }
    return null
  })
  const isFinalAudioMode = computed(() => knowledgeMode.value === 'final-audio' && Boolean(linkedAudioRole.value))

  const linkedAssetTypeLabel = computed(() => getConsistencyAssetTypeLabel(linkedAssetType.value))

  const requestedTab = computed<AssetTab>(() => {
    const raw = String(options.route.query.tab || '').trim()
    if (raw === 'content' || raw === 'image' || raw === 'video' || raw === 'audio' || raw === 'project') {
      return raw as AssetTab
    }
    if (isFinalAudioMode.value) {
      return 'audio'
    }
    return (isSceneImageMode.value || isConsistencyAssetImageMode.value) ? 'image' : 'project'
  })

  const linkedBannerTitle = computed(() => {
    if (isFinalAudioMode.value) {
      return `当前正在为项目 ${linkedProjectId.value} 选择${linkedAudioRole.value === 'narration' ? '旁白' : 'BGM'}素材`
    }
    if (isSceneImageMode.value && linkedSceneNumber.value) {
      return `当前正在为项目 ${linkedProjectId.value} 的第 ${linkedSceneNumber.value} 个分镜挑图`
    }
    if (isConsistencyAssetImageMode.value && linkedAssetType.value) {
      return `当前正在为项目 ${linkedProjectId.value} 的${linkedAssetTypeLabel.value}“${linkedAssetName.value || linkedAssetId.value}”挑选官方参考图`
    }
    return `当前正在为项目 ${linkedProjectId.value} 补充参考素材`
  })

  const linkedBannerDescription = computed(() => {
    if (isFinalAudioMode.value) {
      return linkedAudioRole.value === 'narration'
        ? '这里选中的音频会直接回填到终版音频工作台的旁白槽位，随后你可以继续生成混音预览。'
        : '这里选中的音频会直接回填到终版音频工作台的 BGM 槽位，随后你可以继续生成混音预览。'
    }
    if (isSceneImageMode.value && linkedSceneNumber.value) {
      return '优先在“图片素材”里挑选本地上传和已存在的项目图片；需要补案例时，再切到“项目参考”。'
    }
    if (isConsistencyAssetImageMode.value) {
      return `这里选中的图片会直接回填到当前${linkedAssetTypeLabel.value}资产卡片，作为后续镜头生成优先使用的官方参考图。`
    }
    return '在这里选中的内容、图片和项目经验都可以直接带回当前工作台继续使用。'
  })

  function returnToWorkspace() {
    if (returnPath.value) {
      void options.router.push(returnPath.value)
      return
    }
    if (linkedProjectId.value) {
      void options.router.push({ path: `/generation/${linkedProjectId.value}`, query: linkedStage.value ? { stage: linkedStage.value } : undefined })
      return
    }
    void options.router.push('/dashboard')
  }

  onMounted(() => {
    const initialQuery = truncateQuery(String(options.route.query.q || ''))
    if (initialQuery) {
      options.queryForm.query = initialQuery
    }
    if (isSceneImageMode.value) {
      options.queryForm.kb_type = 'scene'
    }
    if (linkedProjectId.value) {
      options.imageFilters.scope = 'current_project'
    }
    options.activeTab.value = requestedTab.value
  })

  return {
    isConsistencyAssetImageMode,
    isFinalAudioMode,
    isSceneImageMode,
    linkedAssetId,
    linkedAssetName,
    linkedAudioRole,
    linkedAssetType,
    linkedAssetTypeLabel,
    linkedBannerDescription,
    linkedBannerTitle,
    linkedProjectId,
    linkedSceneNumber,
    linkedStage,
    requestedTab,
    returnPath,
    returnToWorkspace,
  }
}